import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig';

// SOLE legal entity + branding — used on year-end tax statements.
const ORG = {
    name: 'Servants of Our Lady of Evangelization',
    shortName: 'SOLE',
    ein: '33-42127697',
    email: 'info@soleministries.org',
    website: 'soleministries.org',
    // Emblem shown on the statement letterhead (sits on a navy badge).
    logoUrl: 'https://soleministries.org/SOLE-logo.png',
    // Optional mailing address shown on the letterhead; safe to leave blank.
    addressLines: [],
    // Who the statement is signed by. Set signerName/signerTitle to a real
    // officer (e.g. 'Rev. John Smith' / 'Executive Director') when you have one.
    signerName: 'The SOLE Ministries Team',
    signerTitle: '',
};

// SOLE brand palette (mirrors --sole-* tokens in App.css)
const BRAND = {
    navy: '#0f3650',
    navyDeep: '#0d2e4e',
    gold: '#c08d3b',
    goldBright: '#f1b249',
    parchment: '#fbf5ea',
    ink: '#1c2b36',
    muted: '#6b7787',
    hair: '#e7ddc9',
};

const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const longDate = (d) => (toDate(d) || new Date()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const RECURRING_ACTIVE_WINDOW_DAYS = 40;

// ---- formatting / normalizing helpers ----
const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtMoney0 = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const toDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDate = (v) => {
    const d = toDate(v);
    return d ? d.toLocaleDateString('en-US') : '';
};
const fmtDateTime = (v) => {
    const d = toDate(v);
    return d ? d.toLocaleString('en-US') : '';
};

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const monthLabel = (d) => d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

// A donor is identified primarily by email (most stable across Stripe customers),
// then by Stripe customer id, then by name.
const donorKeyOf = (d) => {
    const email = (d?.donor?.email || '').trim().toLowerCase();
    if (email) return email;
    if (d?.stripeCustomerId) return String(d.stripeCustomerId);
    const name = (d?.donor?.name || '').trim().toLowerCase();
    return name || 'unknown';
};

const donorAmount = (d) => {
    const a = Number(d?.amount);
    if (Number.isFinite(a) && a) return a;
    const c = Number(d?.amountCents);
    return Number.isFinite(c) ? c / 100 : 0;
};

// Best-known donor profile from a set of that donor's gifts (most recent wins).
const profileFromGifts = (gifts) => {
    const sorted = [...gifts].sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0));
    const pick = (path) => {
        for (const g of sorted) {
            const v = path.split('.').reduce((o, k) => (o ? o[k] : undefined), g);
            if (v) return v;
        }
        return '';
    };
    return {
        name: pick('donor.name') || pick('donor.email') || 'Unknown donor',
        email: pick('donor.email'),
        phone: pick('donor.phone'),
        address1: pick('donor.address1'),
        address2: pick('donor.address2'),
        city: pick('donor.city'),
        state: pick('donor.state'),
        postalCode: pick('donor.postalCode'),
        country: pick('donor.country'),
    };
};

const addressOneLine = (p) => [
    [p.address1, p.address2].filter(Boolean).join(' '),
    [p.city, p.state].filter(Boolean).join(', '),
    p.postalCode,
].filter(Boolean).join(' · ');

const RANGE_LABELS = { all: 'All time', ytd: 'Year to date', mtd: 'Month to date', today: 'Today' };

const emptyManual = () => ({
    name: '', email: '', phone: '',
    address1: '', address2: '', city: '', state: '', postalCode: '', country: 'US',
    amount: '', date: new Date().toISOString().slice(0, 10),
    method: 'check', recurring: false, note: '',
});

const DonationReport = () => {
    const [donations, setDonations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDonorKey, setSelectedDonorKey] = useState(null);
    const [timeRange, setTimeRange] = useState('ytd');

    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState('');

    const [showManual, setShowManual] = useState(false);
    const [manual, setManual] = useState(emptyManual);
    const [savingManual, setSavingManual] = useState(false);

    const currentYear = new Date().getFullYear();
    const [showTax, setShowTax] = useState(false);
    const [taxYear, setTaxYear] = useState(currentYear);

    useEffect(() => {
        const q = query(collection(db, 'donations'), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(
            q,
            (snap) => {
                const rows = [];
                snap.forEach((docSnap) => {
                    const data = docSnap.data() || {};
                    rows.push({ id: docSnap.id, ...data, createdAt: toDate(data.createdAt) });
                });
                setDonations(rows);
                setLoading(false);
            },
            (err) => {
                console.error('donations onSnapshot error:', err);
                setLoading(false);
            }
        );
        return () => unsub();
    }, []);

    useEffect(() => { setSelectedDonorKey(null); }, [timeRange]);

    // ---- range filter ----
    const rangeStart = useMemo(() => {
        const now = new Date();
        if (timeRange === 'today') return startOfDay(now);
        if (timeRange === 'mtd') return startOfMonth(now);
        if (timeRange === 'ytd') return startOfYear(now);
        return null;
    }, [timeRange]);

    const filteredDonations = useMemo(() => {
        if (!rangeStart) return donations;
        return donations.filter((d) => d.createdAt && d.createdAt >= rangeStart);
    }, [donations, rangeStart]);

    // First-ever gift date per donor (across ALL history) — for "new donors".
    const firstGiftByDonor = useMemo(() => {
        const map = new Map();
        donations.forEach((d) => {
            if (!d.createdAt) return;
            const k = donorKeyOf(d);
            const cur = map.get(k);
            if (!cur || d.createdAt < cur) map.set(k, d.createdAt);
        });
        return map;
    }, [donations]);

    // ---- headline totals for the selected range ----
    const totals = useMemo(() => {
        const list = filteredDonations;
        const amounts = list.map(donorAmount);
        const totalAmount = amounts.reduce((s, n) => s + n, 0);
        const recurring = list.filter((d) => d.recurring);
        const oneTime = list.filter((d) => !d.recurring);
        const recurringAmount = recurring.reduce((s, d) => s + donorAmount(d), 0);
        const donorKeys = new Set(list.map(donorKeyOf));
        const newDonors = [...donorKeys].filter((k) => {
            const first = firstGiftByDonor.get(k);
            return first && (!rangeStart || first >= rangeStart);
        }).length;
        const sortedAmts = [...amounts].sort((a, b) => a - b);
        const median = sortedAmts.length
            ? (sortedAmts.length % 2
                ? sortedAmts[(sortedAmts.length - 1) / 2]
                : (sortedAmts[sortedAmts.length / 2 - 1] + sortedAmts[sortedAmts.length / 2]) / 2)
            : 0;

        return {
            totalAmount,
            txCount: list.length,
            recurringTxCount: recurring.length,
            oneTimeTxCount: oneTime.length,
            recurringAmount,
            oneTimeAmount: totalAmount - recurringAmount,
            donorCount: donorKeys.size,
            newDonors,
            avgGift: list.length ? totalAmount / list.length : 0,
            medianGift: median,
            largestGift: amounts.length ? Math.max(...amounts) : 0,
        };
    }, [filteredDonations, firstGiftByDonor, rangeStart]);

    // ---- active recurring donors + estimated monthly recurring revenue ----
    const recurringHealth = useMemo(() => {
        const bySub = new Map();
        donations.forEach((d) => {
            if (!d.recurring) return;
            const key = d.stripeSubscriptionId || `donor:${donorKeyOf(d)}`;
            const arr = bySub.get(key) || [];
            arr.push(d);
            bySub.set(key, arr);
        });

        const cutoff = new Date(Date.now() - RECURRING_ACTIVE_WINDOW_DAYS * 24 * 60 * 60 * 1000);
        let activeCount = 0;
        let mrr = 0;
        let lapsedCount = 0;

        for (const arr of bySub.values()) {
            const sorted = [...arr].sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0));
            const latest = sorted[0];
            const status = (latest.subscriptionStatus || '').toLowerCase();
            const recent = latest.createdAt && latest.createdAt >= cutoff;
            const active = status === 'active' || status === 'trialing' || (!status && recent);
            if (active) {
                activeCount += 1;
                mrr += donorAmount(latest);
            } else if (status === 'canceled' || status === 'unpaid' || status === 'past_due' || (!recent && !status)) {
                lapsedCount += 1;
            }
        }
        return { activeCount, mrr, lapsedCount, subscriptions: bySub.size };
    }, [donations]);

    // ---- 12-month trend ----
    const monthlyTrend = useMemo(() => {
        const now = new Date();
        const months = [];
        for (let i = 11; i >= 0; i -= 1) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.push({ key: monthKey(d), label: monthLabel(d), oneTime: 0, recurring: 0, total: 0, count: 0 });
        }
        const idx = new Map(months.map((m) => [m.key, m]));
        donations.forEach((d) => {
            if (!d.createdAt) return;
            const m = idx.get(monthKey(d.createdAt));
            if (!m) return;
            const amt = donorAmount(d);
            m.count += 1;
            m.total += amt;
            if (d.recurring) m.recurring += amt; else m.oneTime += amt;
        });
        const max = Math.max(1, ...months.map((m) => m.total));
        return { months, max };
    }, [donations]);

    // ---- year-over-year ----
    const yearly = useMemo(() => {
        const map = new Map();
        donations.forEach((d) => {
            if (!d.createdAt) return;
            const y = d.createdAt.getFullYear();
            const row = map.get(y) || { year: y, total: 0, count: 0, donors: new Set(), oneTime: 0, recurring: 0 };
            const amt = donorAmount(d);
            row.total += amt;
            row.count += 1;
            row.donors.add(donorKeyOf(d));
            if (d.recurring) row.recurring += amt; else row.oneTime += amt;
            map.set(y, row);
        });
        const rows = [...map.values()]
            .map((r) => ({ ...r, donors: r.donors.size }))
            .sort((a, b) => b.year - a.year)
            .slice(0, 5);
        const max = Math.max(1, ...rows.map((r) => r.total));
        return { rows, max };
    }, [donations]);

    // ---- donor index (range-scoped) ----
    const donorsIndex = useMemo(() => {
        const map = new Map();
        filteredDonations.forEach((d) => {
            const key = donorKeyOf(d);
            const arr = map.get(key) || [];
            arr.push(d);
            map.set(key, arr);
        });
        const summary = [];
        for (const [key, arr] of map.entries()) {
            const profile = profileFromGifts(arr);
            summary.push({
                key,
                name: profile.name,
                email: profile.email,
                count: arr.length,
                amount: arr.reduce((s, d) => s + donorAmount(d), 0),
                recurring: arr.some((d) => d.recurring),
            });
        }
        summary.sort((a, b) => b.amount - a.amount);
        return { byKey: map, summary };
    }, [filteredDonations]);

    const selectedDonations = useMemo(
        () => (selectedDonorKey ? donorsIndex.byKey.get(selectedDonorKey) || [] : []),
        [selectedDonorKey, donorsIndex]
    );
    const selectedProfile = useMemo(
        () => (selectedDonations.length ? profileFromGifts(selectedDonations) : null),
        [selectedDonations]
    );
    const selectedTotals = useMemo(() => {
        const totalAmount = selectedDonations.reduce((s, d) => s + donorAmount(d), 0);
        return {
            totalAmount,
            txCount: selectedDonations.length,
            recurringTxCount: selectedDonations.filter((d) => d.recurring).length,
            oneTimeTxCount: selectedDonations.filter((d) => !d.recurring).length,
        };
    }, [selectedDonations]);

    const availableYears = useMemo(() => {
        const set = new Set(donations.map((d) => d.createdAt && d.createdAt.getFullYear()).filter(Boolean));
        set.add(currentYear);
        return [...set].sort((a, b) => b - a);
    }, [donations, currentYear]);

    // ---- per-donor annual giving for a calendar year (tax statements) ----
    const computeDonorAnnual = (year) => {
        const yStart = new Date(year, 0, 1);
        const yEnd = new Date(year + 1, 0, 1);
        const map = new Map();
        donations.forEach((d) => {
            if (!d.createdAt || d.createdAt < yStart || d.createdAt >= yEnd) return;
            const key = donorKeyOf(d);
            const arr = map.get(key) || [];
            arr.push(d);
            map.set(key, arr);
        });
        const rows = [];
        for (const [key, gifts] of map.entries()) {
            const profile = profileFromGifts(gifts);
            const total = gifts.reduce((s, g) => s + donorAmount(g), 0);
            rows.push({
                key,
                profile,
                gifts: [...gifts].sort((a, b) => (toDate(a.createdAt) || 0) - (toDate(b.createdAt) || 0)),
                total,
                count: gifts.length,
                recurringCount: gifts.filter((g) => g.recurring).length,
            });
        }
        rows.sort((a, b) => b.total - a.total);
        return rows;
    };

    const taxRows = useMemo(() => computeDonorAnnual(taxYear), [donations, taxYear]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- actions ----
    const handleSync = async (full) => {
        const label = full
            ? 'Import ALL donation history from Stripe'
            : `Sync ${currentYear} year-to-date donations from Stripe`;
        if (!window.confirm(`${label}?\n\nThis reads from Stripe and adds any missing gifts. Existing records are not duplicated.`)) return;
        setSyncing(true);
        setSyncMsg('');
        try {
            const fn = httpsCallable(getFunctions(), 'syncStripeDonations');
            const res = await fn({ full: !!full });
            const r = res.data || {};
            setSyncMsg(
                `Sync complete (${r.scope || (full ? 'all-time' : 'year-to-date')}) — ${r.oneTime || 0} one-time gift(s), ${r.recurringInvoices || 0} recurring payment(s) across ${r.subscriptionsSeen || 0} subscription(s)${r.errors ? `, ${r.errors} error(s)` : ''}.`
            );
        } catch (err) {
            console.error('sync failed', err);
            setSyncMsg(`Sync failed: ${err?.message || 'unknown error'}`);
        } finally {
            setSyncing(false);
        }
    };

    const saveManualDonation = async (e) => {
        e.preventDefault();
        const amount = Number(String(manual.amount).replace(/[^0-9.]/g, ''));
        if (!amount || amount <= 0) { window.alert('Enter a donation amount.'); return; }
        if (!manual.name.trim() && !manual.email.trim()) { window.alert('Enter at least a donor name or email.'); return; }
        setSavingManual(true);
        try {
            const when = manual.date ? new Date(`${manual.date}T12:00:00`) : new Date();
            await addDoc(collection(db, 'donations'), {
                createdAt: when,
                source: 'manual',
                type: 'donation',
                recurring: !!manual.recurring,
                method: manual.method,
                amount: Number(amount.toFixed(2)),
                amountCents: Math.round(amount * 100),
                currency: 'USD',
                donor: {
                    name: manual.name.trim(),
                    email: manual.email.trim().toLowerCase(),
                    phone: manual.phone.trim(),
                    address1: manual.address1.trim(),
                    address2: manual.address2.trim(),
                    city: manual.city.trim(),
                    state: manual.state.trim(),
                    postalCode: manual.postalCode.trim(),
                    country: manual.country.trim() || 'US',
                },
                note: manual.note.trim(),
                paymentStatus: 'paid',
                enteredByEmail: getAuth().currentUser?.email || '',
            });
            setManual(emptyManual());
            setShowManual(false);
        } catch (err) {
            console.error('saveManualDonation failed', err);
            window.alert('Could not save the donation.');
        } finally {
            setSavingManual(false);
        }
    };

    const exportTransactions = async () => {
        const mod = await import('xlsx');
        const XLSX = mod.default || mod;
        const rows = filteredDonations.map((d) => {
            const p = d.donor || {};
            return {
                Date: fmtDateTime(d.createdAt),
                Donor: p.name || '',
                Email: p.email || '',
                Phone: p.phone || '',
                Amount: donorAmount(d).toFixed(2),
                Type: d.recurring ? 'Recurring' : 'One-time',
                Method: d.method || (d.source === 'stripe' ? 'card' : ''),
                Source: d.source || '',
                Status: d.paymentStatus || d.status || '',
                Address: [p.address1, p.address2, p.city, p.state, p.postalCode].filter(Boolean).join(', '),
                Note: d.note || '',
                'Stripe Customer': d.stripeCustomerId || '',
                'Stripe Subscription': d.stripeSubscriptionId || '',
            };
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ Date: 'No donations in range' }]), 'Transactions');
        XLSX.writeFile(wb, `sole-donations-${timeRange}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const exportYearEndExcel = async () => {
        const mod = await import('xlsx');
        const XLSX = mod.default || mod;
        const rows = taxRows;
        const summary = rows.map((r) => ({
            Donor: r.profile.name,
            Email: r.profile.email,
            Address1: r.profile.address1,
            Address2: r.profile.address2,
            City: r.profile.city,
            State: r.profile.state,
            ZIP: r.profile.postalCode,
            Country: r.profile.country,
            'Gifts': r.count,
            'Recurring gifts': r.recurringCount,
            [`Total ${taxYear}`]: r.total.toFixed(2),
        }));
        const detail = [];
        rows.forEach((r) => {
            r.gifts.forEach((g) => {
                detail.push({
                    Donor: r.profile.name,
                    Email: r.profile.email,
                    Date: fmtDate(g.createdAt),
                    Amount: donorAmount(g).toFixed(2),
                    Type: g.recurring ? 'Recurring' : 'One-time',
                    Method: g.method || (g.source === 'stripe' ? 'card' : ''),
                });
            });
        });
        const grand = rows.reduce((s, r) => s + r.total, 0);

        const coverSheet = XLSX.utils.aoa_to_sheet([
            [`${ORG.name} (${ORG.shortName}) — ${taxYear} Giving Statements`],
            [`EIN ${ORG.ein}`],
            [`Generated ${new Date().toLocaleString('en-US')}`],
            [],
            ['Donors', rows.length],
            ['Grand total', grand.toFixed(2)],
        ]);
        const summarySheet = XLSX.utils.json_to_sheet(summary.length ? summary : [{ Donor: `No donations in ${taxYear}` }]);
        const detailSheet = XLSX.utils.json_to_sheet(detail.length ? detail : [{ Donor: `No donations in ${taxYear}` }]);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, coverSheet, 'Overview');
        XLSX.utils.book_append_sheet(wb, summarySheet, `${taxYear} By Donor`);
        XLSX.utils.book_append_sheet(wb, detailSheet, `${taxYear} Gift Detail`);
        XLSX.writeFile(wb, `sole-year-end-${taxYear}.xlsx`);
    };

    // ---- branded, print-ready year-end statement (one or many donors) ----
    const statementStyles = () => `
        @page { size: Letter; margin: 0.8in 0.85in; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; }
        body {
            font-family: 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Georgia, serif;
            color: ${BRAND.ink}; font-size: 12.25pt; line-height: 1.55;
            -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        .stmt { max-width: 6.9in; margin: 0 auto; }
        .stmt + .stmt { page-break-before: always; }

        .lh { text-align: center; }
        .lh .badge {
            display: inline-flex; align-items: center; justify-content: center;
            width: 66px; height: 66px; border-radius: 16px;
            background: ${BRAND.navy}; overflow: hidden; margin-bottom: 10pt;
        }
        .lh .badge img { width: 66px; height: 66px; object-fit: cover; }
        .lh .org { font-size: 16.5pt; font-weight: 600; color: ${BRAND.navy}; letter-spacing: .01em; }
        .lh .meta {
            font-size: 8.75pt; letter-spacing: .09em; text-transform: uppercase;
            color: ${BRAND.muted}; margin-top: 4pt;
        }
        .lh .addr-line { font-size: 9.5pt; color: ${BRAND.muted}; margin-top: 2pt; }
        .rule-wrap { margin: 13pt 0 0; }
        .rule-wrap hr { border: 0; margin: 0; }
        .rule-wrap .r1 { border-top: 1.6px solid ${BRAND.navy}; }
        .rule-wrap .r2 { border-top: 1px solid ${BRAND.gold}; margin-top: 2.5pt; }

        .meta-row {
            display: flex; justify-content: space-between; align-items: flex-start;
            gap: 24pt; margin: 22pt 0 6pt; font-size: 10.75pt;
        }
        .recipient .rname { font-weight: 600; color: ${BRAND.navy}; }
        .recipient div { line-height: 1.42; }
        .issued { text-align: right; color: ${BRAND.muted}; white-space: nowrap; }
        .issued .lbl { display: block; font-size: 8pt; letter-spacing: .1em; text-transform: uppercase; }

        h1.doc-title {
            text-align: center; color: ${BRAND.navy}; font-size: 13.5pt; font-weight: 600;
            text-transform: uppercase; letter-spacing: .16em; margin: 20pt 0 3pt;
        }
        .doc-sub {
            text-align: center; color: ${BRAND.gold}; font-size: 9.5pt;
            letter-spacing: .12em; text-transform: uppercase; margin-bottom: 16pt;
        }
        p { margin: 0 0 10pt; }

        table.gifts { width: 100%; border-collapse: collapse; margin: 12pt 0 2pt; font-size: 11pt; }
        table.gifts thead th {
            text-align: left; font-size: 8.25pt; text-transform: uppercase; letter-spacing: .1em;
            color: ${BRAND.navy}; padding: 6pt 3pt; border-bottom: 1.5px solid ${BRAND.gold};
        }
        table.gifts th.amt, table.gifts td.amt { text-align: right; font-variant-numeric: tabular-nums; }
        table.gifts tbody td { padding: 6pt 3pt; border-bottom: 1px solid ${BRAND.hair}; }
        table.gifts tfoot td {
            padding: 9pt 3pt 0; font-weight: 700; font-size: 12pt; color: ${BRAND.navy};
            border-top: 2px solid ${BRAND.navy};
        }

        .ack {
            margin: 20pt 0 4pt; padding: 12pt 15pt; background: ${BRAND.parchment};
            border: 1px solid #d9c39a; border-left: 3px solid ${BRAND.gold}; border-radius: 4px;
            font-size: 10.25pt; line-height: 1.5; color: #4a3f2c;
        }
        .ack strong { color: ${BRAND.navy}; }

        .sign { margin-top: 20pt; font-size: 11pt; }
        .sign .grat { margin-bottom: 18pt; }
        .sign .who { font-weight: 600; color: ${BRAND.navy}; }
        .sign .who-sub { color: ${BRAND.muted}; font-size: 9.5pt; }

        .foot {
            margin-top: 24pt; padding-top: 7pt; border-top: 1px solid ${BRAND.hair};
            text-align: center; font-size: 8pt; letter-spacing: .04em; color: #9aa3ac;
        }

        @media screen {
            body { background: #eef0f3; padding: 28px 12px; }
            .stmt {
                background: #fff; padding: 0.7in 0.75in; margin: 0 auto 26px;
                box-shadow: 0 6px 30px rgba(15, 23, 42, .16); border-radius: 3px;
            }
            .stmt + .stmt { page-break-before: auto; }
        }
    `;

    const statementArticle = (row, year) => {
        const p = row.profile || {};
        const first = (p.name || '').trim().split(/\s+/)[0];
        const salutation = first && !first.includes('@') ? first : 'Friend';
        const cityStateZip = [[p.city, p.state].filter(Boolean).join(', '), p.postalCode].filter(Boolean).join(' ');
        const giftRows = row.gifts.map((g) => {
            const kind = g.recurring ? 'Recurring monthly gift' : 'One-time gift';
            const m = (g.method || (g.source === 'stripe' ? 'card' : '')).toLowerCase();
            const mLabel = m && m !== 'card' ? ` &mdash; ${esc(m)}` : '';
            return `<tr><td>${esc(fmtDate(g.createdAt))}</td><td>${kind}${mLabel}</td><td class="amt">${esc(fmtMoney(donorAmount(g)))}</td></tr>`;
        }).join('');

        return `
        <article class="stmt">
            <header class="lh">
                <div class="badge"><img src="${esc(ORG.logoUrl)}" alt="${esc(ORG.shortName)}"></div>
                <div class="org">${esc(ORG.name)}</div>
                <div class="meta">EIN ${esc(ORG.ein)} &nbsp;&bull;&nbsp; ${esc(ORG.website)} &nbsp;&bull;&nbsp; ${esc(ORG.email)}</div>
                ${ORG.addressLines.length ? `<div class="addr-line">${ORG.addressLines.map(esc).join(' &bull; ')}</div>` : ''}
            </header>
            <div class="rule-wrap"><hr class="r1"><hr class="r2"></div>

            <div class="meta-row">
                <div class="recipient">
                    <div class="rname">${esc(p.name || 'Valued Donor')}</div>
                    ${p.address1 ? `<div>${esc(p.address1)}</div>` : ''}
                    ${p.address2 ? `<div>${esc(p.address2)}</div>` : ''}
                    ${cityStateZip ? `<div>${esc(cityStateZip)}</div>` : ''}
                    ${p.email ? `<div>${esc(p.email)}</div>` : ''}
                </div>
                <div class="issued"><span class="lbl">Issued</span>${esc(longDate(new Date()))}</div>
            </div>

            <h1 class="doc-title">${year} Annual Giving Statement</h1>
            <div class="doc-sub">Official Receipt for Charitable Contributions</div>

            <p>Dear ${esc(salutation)},</p>
            <p>Thank you for your faithful generosity to ${esc(ORG.name)} during ${year}.
            Your support forms and sends Catholic evangelists to campuses and communities across the country.
            The gifts below make up your complete record of tax-deductible contributions for the year.</p>

            <table class="gifts">
                <thead><tr><th>Date</th><th>Gift</th><th class="amt">Amount</th></tr></thead>
                <tbody>${giftRows}</tbody>
                <tfoot><tr><td colspan="2">Total contributions &mdash; ${year}</td><td class="amt">${esc(fmtMoney(row.total))}</td></tr></tfoot>
            </table>

            <div class="ack">
                <strong>${esc(ORG.name)}</strong> is a tax-exempt organization described in Section 501(c)(3)
                of the Internal Revenue Code (EIN ${esc(ORG.ein)}), and contributions to it are deductible to the
                extent allowed by law. No goods or services were provided in exchange for these contributions.
                Please retain this statement with your tax records.
            </div>

            <div class="sign">
                <div class="grat">With gratitude and prayers,</div>
                <div class="who">${esc(ORG.signerName)}</div>
                ${ORG.signerTitle ? `<div class="who-sub">${esc(ORG.signerTitle)}</div>` : ''}
            </div>

            <div class="foot">${esc(ORG.name)} &nbsp;&bull;&nbsp; ${esc(ORG.website)} &nbsp;&bull;&nbsp; Questions? ${esc(ORG.email)}</div>
        </article>`;
    };

    const openStatements = (rows, year) => {
        const clean = (rows || []).filter((r) => r && r.total > 0);
        if (!clean.length) { window.alert(`No donations in ${year}.`); return; }
        const w = window.open('', '_blank');
        if (!w) { window.alert('Please allow pop-ups to generate the statement.'); return; }
        const title = clean.length === 1
            ? `${ORG.shortName} ${year} Giving Statement — ${clean[0].profile.name || 'Donor'}`
            : `${ORG.shortName} ${year} Giving Statements (${clean.length})`;
        w.document.write(
            `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>` +
            `<link rel="preconnect" href="https://fonts.googleapis.com">` +
            `<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>` +
            `<link href="https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400&display=swap" rel="stylesheet">` +
            `<style>${statementStyles()}</style></head><body>` +
            clean.map((r) => statementArticle(r, year)).join('') +
            `<script>(function(){var done=false;function go(){if(done)return;done=true;` +
            `setTimeout(function(){try{window.focus();}catch(e){}window.print();},250);}` +
            `if(document.fonts&&document.fonts.ready){document.fonts.ready.then(go);}` +
            `window.addEventListener('load',go);setTimeout(go,3000);})();</script>` +
            `</body></html>`
        );
        w.document.close();
    };

    const printReceipt = (row, year) => openStatements([row], year);

    const printSelectedDonorReceipt = () => {
        if (!selectedProfile) return;
        const yStart = new Date(taxYear, 0, 1);
        const yEnd = new Date(taxYear + 1, 0, 1);
        const gifts = selectedDonations
            .filter((g) => g.createdAt && g.createdAt >= yStart && g.createdAt < yEnd)
            .sort((a, b) => (toDate(a.createdAt) || 0) - (toDate(b.createdAt) || 0));
        if (!gifts.length) { window.alert(`No gifts from this donor in ${taxYear}.`); return; }
        openStatements([{
            profile: selectedProfile,
            gifts,
            total: gifts.reduce((s, g) => s + donorAmount(g), 0),
            count: gifts.length,
        }], taxYear);
    };

    const printAllReceipts = () => {
        const rows = taxRows.filter((r) => r.total > 0);
        if (!rows.length) { window.alert(`No donations in ${taxYear}.`); return; }
        if (!window.confirm(`Generate ${rows.length} statement(s) for ${taxYear} in one printable document?`)) return;
        openStatements(rows, taxYear);
    };

    const rangeName = RANGE_LABELS[timeRange] || 'All time';

    return (
        <div style={{ padding: '0 0 1.25rem 0', width: '100%', gridColumn: '1 / -1' }}>
            <div className="donations-toolbar">
                <div className="donations-toolbar-left">Showing: {rangeName}</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div className="range-toggle">
                        <button className={timeRange === 'today' ? 'range-btn active' : 'range-btn'} onClick={() => setTimeRange('today')}>Today</button>
                        <button className={timeRange === 'mtd' ? 'range-btn active' : 'range-btn'} onClick={() => setTimeRange('mtd')}>This Month</button>
                        <button className={timeRange === 'ytd' ? 'range-btn active' : 'range-btn'} onClick={() => setTimeRange('ytd')}>YTD</button>
                        <button className={timeRange === 'all' ? 'range-btn active' : 'range-btn'} onClick={() => setTimeRange('all')}>All time</button>
                    </div>
                    <button className="btn ghost" onClick={() => setShowManual(true)}>+ Add Donation</button>
                    <button className="btn ghost" onClick={exportTransactions}>Export</button>
                    <button className="btn ghost" onClick={() => setShowTax(true)}>Tax Statements</button>
                    <button className="btn ghost" disabled={syncing} onClick={() => handleSync(true)}>
                        {syncing ? 'Working…' : 'Import all history'}
                    </button>
                    <button className="btn" disabled={syncing} onClick={() => handleSync(false)} title={`Reconcile ${currentYear} year-to-date with Stripe`}>
                        {syncing ? 'Syncing…' : 'Sync year to date'}
                    </button>
                </div>
            </div>

            {syncMsg ? <div className="notice">{syncMsg}</div> : null}

            {/* Headline cards */}
            <div className="cards-row">
                <div className="stat-card">
                    <div className="stat-label">Total donated</div>
                    <div className="stat-value">{fmtMoney(totals.totalAmount)}</div>
                    <div className="stat-sub">{rangeName}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Transactions</div>
                    <div className="stat-value">{totals.txCount}</div>
                    <div className="stat-sub">{totals.oneTimeTxCount} one-time • {totals.recurringTxCount} recurring</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Donors</div>
                    <div className="stat-value">{totals.donorCount}</div>
                    <div className="stat-sub">{totals.newDonors} new this period</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Average gift</div>
                    <div className="stat-value">{fmtMoney(totals.avgGift)}</div>
                    <div className="stat-sub">median {fmtMoney(totals.medianGift)} • max {fmtMoney(totals.largestGift)}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Recurring (one-time)</div>
                    <div className="stat-value">{fmtMoney(totals.recurringAmount)}</div>
                    <div className="stat-sub">one-time {fmtMoney(totals.oneTimeAmount)}</div>
                </div>
            </div>

            {/* Analytics */}
            <div className="analytics-grid">
                <div className="panel">
                    <div className="panel-header"><div className="panel-title">Monthly giving — last 12 months</div></div>
                    <div style={{ padding: '1rem' }}>
                        {monthlyTrend.months.every((m) => m.total === 0) ? (
                            <div style={{ color: '#6b7280' }}>No donations recorded yet.</div>
                        ) : (
                            <div className="bar-chart">
                                {monthlyTrend.months.map((m) => (
                                    <div className="bar-col" key={m.key} title={`${m.label}: ${fmtMoney(m.total)} (${m.count} gifts)`}>
                                        <div className="bar-stack">
                                            <div className="bar recurring" style={{ height: `${(m.recurring / monthlyTrend.max) * 120}px` }} />
                                            <div className="bar onetime" style={{ height: `${(m.oneTime / monthlyTrend.max) * 120}px` }} />
                                        </div>
                                        <div className="bar-label">{m.label}</div>
                                        <div className="bar-amt">{fmtMoney0(m.total)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="legend">
                            <span><i className="sw onetime" /> One-time</span>
                            <span><i className="sw recurring" /> Recurring</span>
                        </div>
                    </div>
                </div>

                <div className="panel">
                    <div className="panel-header"><div className="panel-title">Recurring health</div></div>
                    <div style={{ padding: '1rem', display: 'grid', gap: '0.75rem' }}>
                        <div className="mini-grid">
                            <div className="stat-card compact">
                                <div className="stat-label">Active recurring donors</div>
                                <div className="stat-value">{recurringHealth.activeCount}</div>
                            </div>
                            <div className="stat-card compact">
                                <div className="stat-label">Est. monthly recurring</div>
                                <div className="stat-value">{fmtMoney(recurringHealth.mrr)}</div>
                            </div>
                            <div className="stat-card compact">
                                <div className="stat-label">Annualized</div>
                                <div className="stat-value">{fmtMoney(recurringHealth.mrr * 12)}</div>
                            </div>
                            <div className="stat-card compact">
                                <div className="stat-label">Lapsed / canceled</div>
                                <div className="stat-value">{recurringHealth.lapsedCount}</div>
                            </div>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                            Based on {recurringHealth.subscriptions} recurring plan(s) seen. A plan counts as active when Stripe reports it
                            active or it has paid within {RECURRING_ACTIVE_WINDOW_DAYS} days.
                        </div>
                    </div>
                </div>

                <div className="panel">
                    <div className="panel-header"><div className="panel-title">Year over year</div></div>
                    <div style={{ padding: '1rem' }}>
                        {yearly.rows.length === 0 ? (
                            <div style={{ color: '#6b7280' }}>No history yet.</div>
                        ) : (
                            <div style={{ display: 'grid', gap: '0.6rem' }}>
                                {yearly.rows.map((r) => (
                                    <div key={r.year} className="yoy-row">
                                        <div className="yoy-year">{r.year}</div>
                                        <div className="yoy-track">
                                            <div className="yoy-fill" style={{ width: `${(r.total / yearly.max) * 100}%` }} />
                                        </div>
                                        <div className="yoy-amt">{fmtMoney0(r.total)}</div>
                                        <div className="yoy-sub">{r.donors} donors • {r.count} gifts</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Transactions + donor drilldown */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.65fr 1fr', gap: '1rem', alignItems: 'start' }}>
                <div className="panel">
                    <div className="panel-header">
                        <div>
                            <div className="panel-title">Transactions — {rangeName}</div>
                            <div className="panel-sub">Click a donor to drill into their giving history.</div>
                        </div>
                    </div>
                    {loading ? (
                        <div style={{ padding: '1rem', color: '#6b7280' }}>Loading donations…</div>
                    ) : filteredDonations.length === 0 ? (
                        <div style={{ padding: '1rem', color: '#6b7280' }}>No donations found for this range.</div>
                    ) : (
                        <div style={{ overflowX: 'auto', maxHeight: '520px' }}>
                            <table className="donations-table">
                                <thead>
                                    <tr>
                                        <th>Date</th><th>Donor</th><th>Email</th>
                                        <th style={{ textAlign: 'right' }}>Amount</th><th>Type</th><th>Source</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDonations.map((d) => {
                                        const donorKey = donorKeyOf(d);
                                        return (
                                            <tr key={d.id} className={selectedDonorKey === donorKey ? 'row-selected' : ''}>
                                                <td>{fmtDateTime(d.createdAt)}</td>
                                                <td>
                                                    <button className="linkish" onClick={() => setSelectedDonorKey(donorKey)}>
                                                        {d?.donor?.name || d?.donor?.email || 'Unknown'}
                                                    </button>
                                                </td>
                                                <td style={{ color: '#6b7280' }}>{d?.donor?.email || ''}</td>
                                                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(donorAmount(d))}</td>
                                                <td>{d.recurring ? 'Recurring' : 'One-time'}</td>
                                                <td style={{ textTransform: 'capitalize' }}>{d.source || ''}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="panel">
                    <div className="panel-header" style={{ justifyContent: 'space-between' }}>
                        <div>
                            <div className="panel-title">Donor</div>
                            <div className="panel-sub">Totals, history, and tax statement.</div>
                        </div>
                        <button className="btn" disabled={!selectedProfile} onClick={printSelectedDonorReceipt}>
                            {taxYear} Statement
                        </button>
                    </div>

                    {!selectedProfile ? (
                        <div style={{ padding: '1rem', color: '#6b7280' }}>No donor selected.</div>
                    ) : (
                        <div style={{ padding: '1rem' }}>
                            <div style={{ marginBottom: '0.75rem' }}>
                                <div style={{ fontWeight: 700 }}>{selectedProfile.name}</div>
                                <div style={{ color: '#6b7280' }}>{selectedProfile.email}</div>
                                {addressOneLine(selectedProfile) ? (
                                    <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>{addressOneLine(selectedProfile)}</div>
                                ) : null}
                            </div>
                            <div className="mini-grid" style={{ marginBottom: '0.75rem' }}>
                                <div className="stat-card compact">
                                    <div className="stat-label">Total ({rangeName})</div>
                                    <div className="stat-value">{fmtMoney(selectedTotals.totalAmount)}</div>
                                    <div className="stat-sub">{selectedTotals.txCount} gifts</div>
                                </div>
                                <div className="stat-card compact">
                                    <div className="stat-label">One-time / Recurring</div>
                                    <div className="stat-value">{selectedTotals.oneTimeTxCount} / {selectedTotals.recurringTxCount}</div>
                                </div>
                            </div>
                            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
                                <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Gifts</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '260px', overflowY: 'auto' }}>
                                    {selectedDonations
                                        .slice()
                                        .sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0))
                                        .map((d) => (
                                            <div key={d.id} className="donation-line">
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>
                                                        {fmtMoney(donorAmount(d))}{' '}
                                                        <span style={{ color: '#6b7280', fontWeight: 500 }}>{d.recurring ? '• recurring' : ''}</span>
                                                    </div>
                                                    <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>{fmtDateTime(d.createdAt)}</div>
                                                </div>
                                                <div style={{ color: '#6b7280', fontSize: '0.9rem', textTransform: 'capitalize' }}>{d.source || ''}</div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                            <button className="linkish" style={{ marginTop: '0.75rem' }} onClick={() => setSelectedDonorKey(null)}>Clear selection</button>
                        </div>
                    )}

                    <div style={{ borderTop: '1px solid #e5e7eb', padding: '1rem' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Top donors ({rangeName})</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {donorsIndex.summary.slice(0, 10).map((d) => (
                                <button key={d.key} className="donor-row" onClick={() => setSelectedDonorKey(d.key)}>
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{d.name} {d.recurring ? <span className="pill">recurring</span> : null}</div>
                                        <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>{d.email || d.key}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 800 }}>{fmtMoney(d.amount)}</div>
                                        <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>{d.count} tx</div>
                                    </div>
                                </button>
                            ))}
                            {donorsIndex.summary.length === 0 && <div style={{ color: '#6b7280' }}>No donors yet.</div>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Donation modal */}
            {showManual && (
                <div className="dr-modal-backdrop" onClick={() => setShowManual(false)}>
                    <form className="dr-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveManualDonation}>
                        <h2>Add Donation</h2>
                        <p className="panel-sub" style={{ marginTop: 0 }}>For cash, check, or other gifts received outside Stripe.</p>
                        <div className="form-grid">
                            <label>Donor name<input value={manual.name} onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))} /></label>
                            <label>Email<input type="email" value={manual.email} onChange={(e) => setManual((m) => ({ ...m, email: e.target.value }))} /></label>
                            <label>Amount (USD)<input value={manual.amount} onChange={(e) => setManual((m) => ({ ...m, amount: e.target.value }))} placeholder="100.00" /></label>
                            <label>Date<input type="date" value={manual.date} onChange={(e) => setManual((m) => ({ ...m, date: e.target.value }))} /></label>
                            <label>Method
                                <select value={manual.method} onChange={(e) => setManual((m) => ({ ...m, method: e.target.value }))}>
                                    <option value="check">Check</option>
                                    <option value="cash">Cash</option>
                                    <option value="card">Card (manual)</option>
                                    <option value="stock">Stock / in-kind</option>
                                    <option value="other">Other</option>
                                </select>
                            </label>
                            <label className="chk">
                                <input type="checkbox" checked={manual.recurring} onChange={(e) => setManual((m) => ({ ...m, recurring: e.target.checked }))} />
                                Recurring gift
                            </label>
                            <label className="span2">Address line 1<input value={manual.address1} onChange={(e) => setManual((m) => ({ ...m, address1: e.target.value }))} /></label>
                            <label className="span2">Address line 2<input value={manual.address2} onChange={(e) => setManual((m) => ({ ...m, address2: e.target.value }))} /></label>
                            <label>City<input value={manual.city} onChange={(e) => setManual((m) => ({ ...m, city: e.target.value }))} /></label>
                            <label>State<input value={manual.state} onChange={(e) => setManual((m) => ({ ...m, state: e.target.value }))} /></label>
                            <label>ZIP<input value={manual.postalCode} onChange={(e) => setManual((m) => ({ ...m, postalCode: e.target.value }))} /></label>
                            <label>Phone<input value={manual.phone} onChange={(e) => setManual((m) => ({ ...m, phone: e.target.value }))} /></label>
                            <label className="span2">Note<input value={manual.note} onChange={(e) => setManual((m) => ({ ...m, note: e.target.value }))} placeholder="Check #1234, spring appeal…" /></label>
                        </div>
                        <div className="dr-modal-actions">
                            <button type="button" className="btn ghost" onClick={() => setShowManual(false)}>Cancel</button>
                            <button type="submit" className="btn" disabled={savingManual}>{savingManual ? 'Saving…' : 'Save Donation'}</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Tax statements modal */}
            {showTax && (
                <div className="dr-modal-backdrop" onClick={() => setShowTax(false)}>
                    <div className="dr-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Year-End Tax Statements</h2>
                        <p className="panel-sub" style={{ marginTop: 0 }}>
                            Every donor’s total giving for the calendar year, with the 501(c)(3) acknowledgement language and EIN {ORG.ein}.
                        </p>
                        <label style={{ display: 'block', margin: '0.75rem 0' }}>
                            Tax year&nbsp;
                            <select value={taxYear} onChange={(e) => setTaxYear(Number(e.target.value))}>
                                {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </label>
                        {(() => {
                            const rows = taxRows;
                            const grand = rows.reduce((s, r) => s + r.total, 0);
                            return (
                                <div className="notice" style={{ marginBottom: '0.75rem' }}>
                                    {rows.length} donor(s) • {fmtMoney(grand)} total in {taxYear}
                                </div>
                            );
                        })()}
                        <div className="dr-modal-actions" style={{ flexWrap: 'wrap' }}>
                            <button className="btn ghost" onClick={() => setShowTax(false)}>Close</button>
                            <button className="btn ghost" onClick={printAllReceipts}>Print all statements</button>
                            <button className="btn" onClick={exportYearEndExcel}>Download Excel</button>
                        </div>
                        <div style={{ maxHeight: '320px', overflowY: 'auto', marginTop: '0.75rem' }}>
                            <table className="donations-table">
                                <thead><tr><th>Donor</th><th>Address</th><th style={{ textAlign: 'right' }}>Gifts</th><th style={{ textAlign: 'right' }}>{taxYear} Total</th><th /></tr></thead>
                                <tbody>
                                    {taxRows.map((r) => (
                                        <tr key={r.key}>
                                            <td>
                                                <div style={{ fontWeight: 700 }}>{r.profile.name}</div>
                                                <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>{r.profile.email}</div>
                                            </td>
                                            <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{addressOneLine(r.profile) || '—'}</td>
                                            <td style={{ textAlign: 'right' }}>{r.count}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoney(r.total)}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button className="linkish" onClick={() => printReceipt(r, taxYear)}>Statement</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {taxRows.length === 0 && (
                                        <tr><td colSpan={5} style={{ color: '#6b7280' }}>No donations in {taxYear}.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        .donations-toolbar { display:flex; align-items:center; justify-content:space-between; gap:0.75rem; margin-bottom:0.75rem; flex-wrap:wrap; }
        .donations-toolbar-left { color:#6b7280; font-weight:700; font-size:0.95rem; }
        @media (max-width: 900px) { .donations-toolbar { flex-direction:column; align-items:flex-start; } }
        .range-toggle { display:inline-flex; border:1px solid #e5e7eb; border-radius:999px; padding:0.2rem; gap:0.25rem; }
        .range-btn { border:none; background:transparent; padding:0.45rem 0.75rem; border-radius:999px; font-weight:800; font-size:0.85rem; color:#374151; cursor:pointer; white-space:nowrap; }
        .range-btn:hover { background: rgba(28,54,80,0.08); }
        .range-btn.active { background:#1c3650; color:#fff; }

        .cards-row { display:grid; grid-template-columns:repeat(5, minmax(0,1fr)); gap:0.75rem; margin-bottom:1rem; }
        .analytics-grid { display:grid; grid-template-columns:1.4fr 1fr; gap:1rem; margin-bottom:1rem; }
        .analytics-grid .panel:first-child { grid-row: span 2; }
        .mini-grid { display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:0.6rem; }

        .panel { background:#fff; border:1px solid #c08d3b; border-radius:14px; overflow:hidden; box-shadow:0 8px 18px rgba(15,23,42,0.04); }
        .panel-header { padding:1rem; display:flex; align-items:center; gap:0.75rem; border-bottom:1px solid #e5e7eb; }
        .panel-title { font-weight:800; color:#0f3650; }
        .panel-sub { color:#6b7280; font-size:0.92rem; }

        .stat-card { background:#fff; border:1px solid #c08d3b; border-radius:14px; padding:0.9rem 1rem; }
        .stat-card.compact { padding:0.7rem 0.8rem; }
        .stat-label { color:#6b7280; font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; }
        .stat-value { font-size:1.3rem; font-weight:900; margin-top:0.2rem; color:#0f3650; }
        .stat-sub { color:#6b7280; font-size:0.85rem; margin-top:0.2rem; }

        .bar-chart { display:flex; align-items:flex-end; gap:0.4rem; height:170px; }
        .bar-col { flex:1; display:flex; flex-direction:column; align-items:center; gap:2px; }
        .bar-stack { display:flex; flex-direction:column-reverse; justify-content:flex-start; height:130px; width:100%; }
        .bar { width:100%; border-radius:3px 3px 0 0; }
        .bar.onetime { background:#0f3650; }
        .bar.recurring { background:#c08d3b; }
        .bar-label { font-size:0.68rem; color:#6b7280; }
        .bar-amt { font-size:0.66rem; color:#9ca3af; }
        .legend { display:flex; gap:1rem; margin-top:0.75rem; font-size:0.8rem; color:#6b7280; }
        .legend .sw { display:inline-block; width:10px; height:10px; border-radius:2px; margin-right:4px; }
        .legend .sw.onetime { background:#0f3650; }
        .legend .sw.recurring { background:#c08d3b; }

        .yoy-row { display:grid; grid-template-columns:48px 1fr auto; grid-template-areas:"y t a" "y s s"; align-items:center; gap:0.25rem 0.6rem; }
        .yoy-year { grid-area:y; font-weight:800; color:#0f3650; }
        .yoy-track { grid-area:t; height:10px; background:rgba(15,23,42,0.08); border-radius:999px; overflow:hidden; }
        .yoy-fill { height:100%; background:#0f3650; }
        .yoy-amt { grid-area:a; font-weight:800; }
        .yoy-sub { grid-area:s; font-size:0.8rem; color:#6b7280; }

        .donations-table { width:100%; border-collapse:collapse; }
        .donations-table th { text-align:left; font-size:0.75rem; color:#c08d3b; text-transform:uppercase; letter-spacing:0.04em; padding:0.7rem 1rem; border-bottom:1px solid rgba(192,141,59,0.3); background:rgba(241,178,73,0.06); white-space:nowrap; position:sticky; top:0; }
        .donations-table td { padding:0.7rem 1rem; border-bottom:1px solid #eef2f7; vertical-align:top; white-space:nowrap; }
        .donations-table tr:hover td { background: rgba(241,178,73,0.08); }
        .donations-table .row-selected td { background: rgba(241,178,73,0.14); }

        .linkish { background:none; border:none; padding:0; margin:0; color:#0f3650; font-weight:800; cursor:pointer; text-align:left; }
        .linkish:hover { text-decoration:underline; }
        .pill { font-size:0.65rem; background:#c08d3b; color:#fff; border-radius:999px; padding:1px 7px; vertical-align:middle; font-weight:800; text-transform:uppercase; }

        .btn { background:#1c3650; color:#fff; border:none; border-radius:10px; padding:0.55rem 0.85rem; font-weight:800; cursor:pointer; font-size:0.85rem; }
        .btn.ghost { background:#fff; color:#0f3650; border:1px solid #c08d3b; }
        .btn:disabled { opacity:0.55; cursor:not-allowed; }
        .notice { background:#fffaf0; border:1px solid #f1b249; color:#8a5a12; border-radius:10px; padding:0.6rem 0.8rem; font-size:0.88rem; margin-bottom:0.75rem; }

        .donation-line { display:flex; align-items:center; justify-content:space-between; gap:0.75rem; padding:0.6rem 0.7rem; border:1px solid #eef2f7; border-radius:12px; background:#fbfcff; }
        .donor-row { border:1px solid #eef2f7; border-radius:12px; background:#fbfcff; padding:0.6rem 0.7rem; display:flex; align-items:center; justify-content:space-between; gap:0.75rem; cursor:pointer; text-align:left; }
        .donor-row:hover { background:#f8fafc; }

        .dr-modal-backdrop { position:fixed; inset:0; background:rgba(2,6,23,0.45); display:flex; align-items:flex-start; justify-content:center; padding:36px 16px; z-index:9999; overflow-y:auto; }
        .dr-modal { background:#fff; border-radius:16px; padding:1.25rem; width:min(680px,100%); box-shadow:0 24px 60px rgba(2,6,23,0.35); }
        .dr-modal h2 { margin:0 0 0.25rem; color:#0f3650; }
        .form-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:0.6rem; margin:0.75rem 0; }
        .form-grid label { display:flex; flex-direction:column; font-size:0.8rem; font-weight:700; color:#374151; gap:0.2rem; }
        .form-grid label.span2 { grid-column:1 / -1; }
        .form-grid label.chk { flex-direction:row; align-items:center; gap:0.4rem; }
        .form-grid input, .form-grid select { border:1px solid #d1d5db; border-radius:8px; padding:0.45rem 0.5rem; font-size:0.9rem; font-weight:500; }
        .dr-modal-actions { display:flex; justify-content:flex-end; gap:0.5rem; margin-top:0.5rem; }

        @media (max-width: 1100px) {
          .cards-row { grid-template-columns:repeat(2, minmax(0,1fr)); }
          .analytics-grid { grid-template-columns:1fr; }
          .analytics-grid .panel:first-child { grid-row:auto; }
          div[style*="grid-template-columns: 1.65fr 1fr"] { grid-template-columns:1fr !important; }
        }
      `}</style>
        </div>
    );
};

export default DonationReport;
